import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { IPage } from '../../../models/page';
import Title from '../../../components/UI/Title/Title';
import Filters from '../../../components/Container/Filters/Filters';
import { FieldTypes, IFormFields } from '../../../models/form';
import { useAppSelector } from '../../../store';
import { IButton } from '../../../models/button';
import { waitingListReportValidation } from '../../../utils/formValidation/waitingListReport';
import { ReportsActions } from '../../../store/entities/reports/reports.actions';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import { SitesActions } from '../../../store/entities/sites/sites.actions';
import { ISite } from '../../../models/site';
import { IDynamicList } from '../../../store/entities/general/type';
import './WaitingListReports.scss';
import Button from '../../../components/UI/Button/Button';

// Component: WaitingListReport
// Allows filtering by date range, Sites (siteIds), Compounds (compoundIds), WaitingListTypes, WaitingListStatuses
// Types & Statuses are fetched via dynamic lists (already exposed on general store)

const WaitingListReports = ({ permission, nextRoute }: IPage) => {
  const dispatch = useDispatch();
  const { formikProps } = useAppSelector((store) => store.form);
  const {
    appLanguage,
    waitingListTypes,
    waitingListStatuses,
    siteCompounds,
    compoundNames,
  } = useAppSelector((state) => state.general);
  const { sites } = useAppSelector((state) => state.sites);

  const fromCreatedDate = formikProps?.values?.fromCreatedDate;
  const toCreatedDate = formikProps?.values?.toCreatedDate;

  const [siteList, setSiteList] = useState<IAutoCompleteItem[]>([]);
  const [compoundList, setCompoundList] = useState<IAutoCompleteItem[]>([]);
  const [compoundsLoading, setCompoundsLoading] = useState<boolean>(false);
  const [typeList, setTypeList] = useState<IAutoCompleteItem[]>([]);
  const [statusList, setStatusList] = useState<IAutoCompleteItem[]>([]);
  const lastFetchedSitesRef = useRef<string>('');

  // Fetch initial data
  useEffect(() => {
    dispatch(SitesActions.getSites());
    dispatch(GeneralActions.getWaitingListTypes());
    dispatch(GeneralActions.getWaitingListStatuses());
    dispatch(GeneralActions.getCompoundNames());
  }, [dispatch]);

  // Build sites list
  useEffect(() => {
    if (sites?.length) {
      const newSites: IAutoCompleteItem[] = sites
        .filter((s: ISite) => s.isActive)
        .map((s: ISite) => ({
          id: s.id as number,
          label: appLanguage === 'hebrew' ? s.nameHE : (s.nameEN ?? ''),
        }));
      setSiteList(newSites);
    }
  }, [sites, appLanguage]);

  // Build dynamic types list
  useEffect(() => {
    if (waitingListTypes?.length) {
      setTypeList(
        waitingListTypes.map((t: IDynamicList) => ({
          id: t.id,
          label: appLanguage === 'hebrew' ? t.nameHE : (t.nameEN ?? ''),
        }))
      );
    }
  }, [waitingListTypes, appLanguage]);

  // Build dynamic statuses list
  useEffect(() => {
    if (waitingListStatuses?.length) {
      setStatusList(
        waitingListStatuses.map((s: IDynamicList) => ({
          id: s.id,
          label: appLanguage === 'hebrew' ? s.nameHE : (s.nameEN ?? ''),
        }))
      );
    }
  }, [waitingListStatuses, appLanguage]);

  // Fetch compounds for selected sites (single or multiple)
  useEffect(() => {
    const selectedSiteIds: number[] = formikProps?.values?.siteIds || [];
    const key = selectedSiteIds.slice().sort().join(',');
    if (key === lastFetchedSitesRef.current) return; // no change
    lastFetchedSitesRef.current = key;

    const fetchCompounds = async () => {
      setCompoundsLoading(true);
      try {
        if (selectedSiteIds.length === 1) {
          // Leverage store for single site (will populate siteCompounds later)
          await dispatch(
            GeneralActions.getCompoundBySite(String(selectedSiteIds[0]))
          );
        } else {
          formikProps?.setFieldValue?.('compoundIds', null);
        }
      } finally {
        setCompoundsLoading(false);
      }
    };
    fetchCompounds();
  }, [formikProps?.values?.siteIds, dispatch, appLanguage]);

  // Build compounds list from store only for single-site selection
  useEffect(() => {
    const selectedSiteIds: number[] = formikProps?.values?.siteIds || [];
    if (selectedSiteIds.length === 1 && siteCompounds?.length) {
      setCompoundList(
        siteCompounds
          .filter((c: any) => !c.isLockedList)
          .map((c: any) => {
            const compoundName = compoundNames?.find(
              (name) => name.id === c.compoundNameId
            );
            return {
              id: c.id,
              label:
                appLanguage === 'hebrew'
                  ? (compoundName?.nameHE ?? '')
                  : (compoundName?.nameEN ?? ''),
            };
          })
      );
    } else {
      setCompoundList([]);
    }
  }, [siteCompounds, appLanguage, formikProps?.values?.siteIds]);

  const searchFields: IFormFields = {
    initialValues: {
      fromCreatedDate: null,
      toCreatedDate: null,
      siteIds: null,
      compoundIds: null,
      waitingListTypeIds: null,
      waitingListStatusIds: null,
    },
    formData: [
      {
        type: FieldTypes.DATE,
        id: 'fromCreatedDate',
        label: 'searchFields.fromCreatedDate',
        props: {
          getStartOfDay: true,
          defaultTime: new Date(0, 0, 0, 0, 0, 0),
        },
      },
      {
        type: FieldTypes.DATE,
        id: 'toCreatedDate',
        label: 'searchFields.toCreatedDate.label',
        props: {
          getFullDayTime: true,
          numOfDays: 1,
          minDate: new Date(fromCreatedDate),
          defaultTime: new Date(0, 0, 0, 23, 59, 59),
        },
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: 'siteIds',
        label: 'searchFields.sites',
        items: siteList,
        props: { multiple: true },
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: 'compoundIds',
        label: 'searchFields.compounds',
        items: compoundList,
        props: { multiple: true },
        disabled: compoundList?.length === 0 || compoundsLoading,
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: 'waitingListTypeIds',
        label: 'searchFields.waitingListTypes',
        items: typeList,
        props: { multiple: true },
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: 'waitingListStatusIds',
        label: 'searchFields.waitingListStatuses',
        items: statusList,
        props: { multiple: true },
      },
    ],
  };

  const exportWaitingListReport = async (values: any) => {
    values ??= {};
    dispatch(
      ReportsActions.downloadWaitingListReport({
        fromCreatedDate: values.fromCreatedDate,
        toCreatedDate: values.toCreatedDate,
        siteIds: values.siteIds,
        compoundIds: values.compoundIds,
        waitingListTypeIds: values.waitingListTypeIds,
        waitingListStatusIds: values.waitingListStatusIds,
      })
    );
  };

  const submitButton: IButton = {
    title: 'button.exportCSV',
    type: 'submit',
    disabled: !waitingListReportValidation.isValidSync(formikProps?.values),
  };

  return (
    <div className="waitingListReport__wrapper">
      <Title text="reports.waitingListReport.pageTitle" />
      <Filters
        fields={searchFields}
        filtersValidation={waitingListReportValidation}
        onSubmit={exportWaitingListReport}
        showSubmitButton={true}
        overridedSubmitButton={<Button {...submitButton} outlined />}
      />
    </div>
  );
};

export default WaitingListReports;
